import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Calendar, 
  Play, 
  Clock, 
  Bot, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  PoundSterling,
  Users,
  Home,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FiscalAutomationDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState(null);
  const [engineStatus, setEngineStatus] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [agentStatus, setAgentStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [reconciliation, setReconciliation] = useState([]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statusRes, calendarRes, agentRes, runsRes, reconRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/fiscal-automation/status`, { headers }),
        fetch(`${API_URL}/api/admin/fiscal-automation/calendar`, { headers }),
        fetch(`${API_URL}/api/admin/fiscal-automation/ai-agents/status`, { headers }),
        fetch(`${API_URL}/api/admin/fiscal-automation/runs?limit=20`, { headers }),
        fetch(`${API_URL}/api/admin/fiscal-automation/reconciliation/summary?months=6`, { headers })
      ]);

      if (statusRes.ok) setEngineStatus(await statusRes.json());
      if (calendarRes.ok) setCalendar(await calendarRes.json());
      if (agentRes.ok) setAgentStatus(await agentRes.json());
      if (runsRes.ok) setRuns((await runsRes.json()).runs || []);
      if (reconRes.ok) setReconciliation((await reconRes.json()).reconciliation_runs || []);
    } catch (error) {
      toast.error('Failed to fetch fiscal data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const triggerJob = async (jobName) => {
    setTriggerLoading(jobName);
    try {
      const response = await fetch(`${API_URL}/api/admin/fiscal-automation/trigger`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ job_name: jobName })
      });
      
      if (response.ok) {
        toast.success(`Job "${jobName}" triggered successfully`);
        fetchData();
      } else {
        toast.error(`Failed to trigger job "${jobName}"`);
      }
    } catch (error) {
      toast.error('Failed to trigger job');
    } finally {
      setTriggerLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'completed': 'bg-green-100 text-green-800',
      'running': 'bg-blue-100 text-blue-800',
      'failed': 'bg-red-100 text-red-800',
      'partial': 'bg-yellow-100 text-yellow-800',
      'requires_review': 'bg-orange-100 text-orange-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100 text-gray-800'}>{status}</Badge>;
  };

  const jobDescriptions = {
    rent_collection: { icon: PoundSterling, desc: 'Trigger GoCardless Direct Debit collection' },
    arrears_check: { icon: AlertTriangle, desc: 'Check for overdue payments, trigger JAMES' },
    landlord_payout: { icon: Users, desc: 'Calculate and process landlord payouts via Griffin' },
    contractor_payout: { icon: Home, desc: 'Process approved contractor invoices' },
    reconciliation: { icon: RefreshCw, desc: 'Match expected vs actual payments' },
    statement_generation: { icon: FileText, desc: 'Generate monthly PDF statements for landlords' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="fiscal-automation-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fiscal Automation</h1>
          <p className="text-gray-500">Automated financial processes powered by APScheduler</p>
        </div>
        <Button onClick={fetchData} variant="outline" data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Engine Status */}
      <Card data-testid="engine-status-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            APScheduler Fiscal Engine
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Badge className={engineStatus?.is_running ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
              {engineStatus?.is_running ? 'RUNNING' : 'STOPPED'}
            </Badge>
            <span className="text-sm text-gray-500">
              {engineStatus?.scheduled_jobs?.length || 0} scheduled jobs
            </span>
          </div>
          
          {engineStatus?.scheduled_jobs && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-gray-700">Scheduled Jobs:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {engineStatus.scheduled_jobs.map((job) => (
                  <div key={job.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{job.name}</span>
                    <span className="text-xs text-gray-500">
                      Next: {job.next_run ? new Date(job.next_run).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="calendar" data-testid="fiscal-tabs">
        <TabsList>
          <TabsTrigger value="calendar">Fiscal Calendar</TabsTrigger>
          <TabsTrigger value="agents">AI Agents</TabsTrigger>
          <TabsTrigger value="runs">Run History</TabsTrigger>
          <TabsTrigger value="reconciliation">Reconciliation</TabsTrigger>
        </TabsList>

        {/* Fiscal Calendar Tab */}
        <TabsContent value="calendar" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Monthly Fiscal Calendar
              </CardTitle>
              <CardDescription>
                Configured fiscal event days and next run dates
              </CardDescription>
            </CardHeader>
            <CardContent>
              {calendar?.config && (
                <div className="space-y-4">
                  {Object.entries(calendar.config).map(([key, value]) => {
                    const JobIcon = jobDescriptions[key]?.icon || Clock;
                    return (
                      <div key={key} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`calendar-${key}`}>
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <JobIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium capitalize">{key.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-gray-500">{jobDescriptions[key]?.desc || value.description}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">Day {value.day}</p>
                          <p className="text-sm text-gray-500">
                            Next: {value.next_run} ({value.days_until} days)
                          </p>
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => triggerJob(key)}
                          disabled={triggerLoading === key}
                          data-testid={`trigger-${key}`}
                        >
                          {triggerLoading === key ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Agents Tab */}
        <TabsContent value="agents" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5" />
                AI Agents - Fiscal Integration
              </CardTitle>
              <CardDescription>
                Autonomous agents integrated into fiscal processes
              </CardDescription>
            </CardHeader>
            <CardContent>
              {agentStatus?.agents && (
                <div className="space-y-4">
                  {agentStatus.agents.map((agent) => (
                    <div key={agent.name} className="p-4 border rounded-lg" data-testid={`agent-${agent.name}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-lg">{agent.name}</h3>
                          <Badge className="bg-green-100 text-green-800">{agent.status}</Badge>
                        </div>
                        <Badge variant="outline">{agent.autonomy_level}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{agent.full_name}</p>
                      <div className="mt-2">
                        <p className="text-sm font-medium text-gray-700">Fiscal Responsibilities:</p>
                        <ul className="mt-1 space-y-1">
                          {agent.fiscal_responsibilities.map((resp, idx) => (
                            <li key={idx} className="text-sm text-gray-600 flex items-center gap-2">
                              <CheckCircle className="h-3 w-3 text-green-500" />
                              {resp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {agentStatus?.autonomous_capabilities && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Autonomous Capabilities</h4>
                  <ul className="space-y-1">
                    {agentStatus.autonomous_capabilities.map((cap, idx) => (
                      <li key={idx} className="text-sm text-blue-800 flex items-center gap-2">
                        <CheckCircle className="h-3 w-3" />
                        {cap}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Run History Tab */}
        <TabsContent value="runs" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Fiscal Run History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {runs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No fiscal runs recorded yet</p>
              ) : (
                <div className="space-y-2">
                  {runs.map((run) => (
                    <div key={run.id} className="flex items-center justify-between p-3 border rounded" data-testid={`run-${run.id}`}>
                      <div>
                        <p className="font-medium capitalize">{run.event_type.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-gray-500">{run.started_at ? new Date(run.started_at).toLocaleString() : 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(run.status)}
                        <p className="text-sm text-gray-500 mt-1">
                          {run.records_processed} records | £{run.total_amount}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reconciliation Tab */}
        <TabsContent value="reconciliation" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" />
                Reconciliation Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reconciliation.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No reconciliation runs recorded yet</p>
              ) : (
                <div className="space-y-3">
                  {reconciliation.map((rec) => (
                    <div key={rec.id} className="p-4 border rounded-lg" data-testid={`recon-${rec.id}`}>
                      <div className="flex justify-between items-center mb-2">
                        <p className="font-medium">{rec.period}</p>
                        {getStatusBadge(rec.status)}
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-2">
                        <div>
                          <p className="text-xs text-gray-500">Expected</p>
                          <p className="font-medium">£{rec.expected_rent}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Collected</p>
                          <p className="font-medium">£{rec.collected_rent}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Collection Rate</p>
                          <p className="font-medium">{rec.collection_rate_percent}%</p>
                        </div>
                      </div>
                      {rec.discrepancies_found > 0 && (
                        <p className="text-sm text-orange-600 mt-2">
                          ⚠️ {rec.discrepancies_found} discrepancies found
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FiscalAutomationDashboard;
